<template>
  <div class="chat-human-phase">
    <!-- ✅ REMOVED: Agent banner - now handled by NewChatBody -->
    <!-- This component now only handles the queuing logic behind the scenes -->
  </div>
</template>
<script>
// ChatPhase/ChatHuman.vue - FIXED SCRIPT SECTION
import {
  subscribeToConversation,
  handleAgentJoinedChat,
} from "../../../lib/appwriteChat";
import { appwriteConfig } from "../../../lib/appwrite";

// ✅ Import client for message subscription
import { Client } from "appwrite";
const client = new Client();
client
  .setEndpoint(appwriteConfig.endpoint)
  .setProject(appwriteConfig.projectId);
// ChatPhase/ChatHuman.vue - FIXED to prevent duplicate agent join on reload

// ChatPhase/ChatHuman.vue - FINAL FIX - No duplicate agent join on reload

export default {
  name: 'ChatHuman',
  props: {
    styles: {
      type: Object,
      default: () => ({})
    },
    customer: {
      type: Object,
      required: true
    },
    conversation: {
      type: Object,
      required: true
    },
    companyId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      agentInfo: null,
      subscriptionUnsubscribe: null,
      pendingCustomerMessages: [],
      messageSubscription: null,
      isAgentFullyJoined: false,
      hasEmittedAgentJoined: false,
      isInitialLoad: true,
      participantCount: 0,
      hasProcessedInitialState: false, // ✅ Track if we've processed initial state
      agentDetectionTimeout: null // ✅ Timeout for agent detection
    }
  },
  emits: ['add-message', 'agent-joined', 'phase-complete'],
  mounted() {
    console.log('🤝 ChatHuman component mounted');
    console.log('🤝 Initial conversation data:', {
      participants: this.conversation.participants?.length || 0,
      hasAgentName: !!this.conversation.agentName,
      hasAgentProfile: !!this.conversation.agentProfile
    });
    
    // ✅ Set initial participant count
    this.participantCount = this.conversation.participants?.length || 1;
    
    // ✅ CRITICAL: Check if agent is already present in any form
    const hasExistingAgent = this.conversation.participants?.length > 1 || 
                            this.conversation.agentName || 
                            this.conversation.agentProfile;
    
    if (hasExistingAgent) {
      console.log('👤 Agent already present on page load - marking as joined');
      this.hasEmittedAgentJoined = true; // Prevent future join events
      this.isAgentFullyJoined = true;
    }
    
    this.initializeHumanChat();
    this.subscribeToMessages();
    
    // ✅ Safety timeout - if no real agent join detected in 3 seconds, consider existing
    this.agentDetectionTimeout = setTimeout(() => {
      if (!this.hasEmittedAgentJoined && this.participantCount > 1) {
        console.log('⏰ Timeout reached - treating as existing agent');
        this.hasEmittedAgentJoined = true;
        this.isAgentFullyJoined = true;
      }
    }, 3000);
  },
  beforeUnmount() {
    this.cleanup();
  },
  methods: {
    async initializeHumanChat() {
      try {
        // ✅ Check if conversation already has agent info
        await this.checkConversationAgentInfo();
        
        // Subscribe to conversation updates to detect agent joining
        this.subscriptionUnsubscribe = subscribeToConversation(
          this.conversation.$id,
          this.handleConversationUpdate
        );

        // ✅ Check if agent already joined
        await this.checkExistingAgent();

        // ✅ Mark initial load as complete
        setTimeout(() => {
          this.isInitialLoad = false;
          this.hasProcessedInitialState = true;
          console.log('🤝 ChatHuman initial load complete');
        }, 2000); // Longer delay to ensure all data is loaded

      } catch (error) {
        console.error('❌ Error initializing human chat:', error);
      }
    },

    // ✅ Subscribe to new messages
    subscribeToMessages() {
      try {
        const channelId = `databases.${appwriteConfig.databaseId}.collections.${appwriteConfig.messageCollectionId}.documents`;
        
        this.messageSubscription = client.subscribe(channelId, (response) => {
          // Only process if initial state has been processed
          if (!this.hasProcessedInitialState) {
            console.log('📨 Message received but initial state not processed yet, queuing...');
            return;
          }
          
          console.log('📨 New message received in ChatHuman:', response);
          
          if (response.payload.conversation_id === this.conversation.$id) {
            this.handleNewMessage(response.payload);
          }
        });
        
        console.log(`✅ ChatHuman subscribed to messages for conversation: ${this.conversation.$id}`);
      } catch (error) {
        console.error('❌ Error subscribing to messages:', error);
      }
    },

    // ✅ Handle new messages - only emit for genuinely new agents
    handleNewMessage(message) {
      console.log('📩 ChatHuman processing new message:', {
        sender_type: message.sender_type,
        sender_id: message.sender_id,
        content: message.content?.substring(0, 50) + '...',
        hasEmittedAgentJoined: this.hasEmittedAgentJoined,
        hasProcessedInitialState: this.hasProcessedInitialState
      });
      
      // ✅ Detect HUMAN AGENT messages
      if (message.sender_type === 'agent' && message.sender_id !== 'ai' && !message.sender_id.includes('ai')) {
        console.log('👤 HUMAN AGENT message detected!');
        
        // ✅ ONLY emit if we haven't already AND initial state is processed
        if (!this.hasEmittedAgentJoined && this.hasProcessedInitialState) {
          console.log('🎉 NEW HUMAN AGENT DETECTED - Emitting agent joined!');
          
          this.agentInfo = {
            agentName: message.sender_name || 'Support Agent',
            avatar: message.sender_avatar || null,
            agentId: message.sender_id
          };
          
          this.isAgentFullyJoined = true;
          this.hasEmittedAgentJoined = true;
          
          // Clear timeout since we detected a real new agent
          if (this.agentDetectionTimeout) {
            clearTimeout(this.agentDetectionTimeout);
          }
          
          // ✅ EMIT AGENT JOINED EVENT
          this.$emit('agent-joined', {
            agentInfo: this.agentInfo,
            agentId: message.sender_id
          });
          
          console.log('✅ Agent joined event emitted:', this.agentInfo);
          this.processQueuedMessages();
          return;
        } else {
          console.log('👤 Agent message but not emitting join (existing agent or not ready)');
          // Still set agent info if we don't have it
          if (!this.agentInfo) {
            this.agentInfo = {
              agentName: message.sender_name || 'Support Agent',
              avatar: message.sender_avatar || null,
              agentId: message.sender_id
            };
            this.isAgentFullyJoined = true;
          }
        }
      }
      
      // Handle other message types
      this.sendMessageToNewChatBody(message);
    },

    // ✅ Send message to NewChatBody
    sendMessageToNewChatBody(messageData) {
      this.$emit('add-message', {
        type: messageData.sender_type,
        content: messageData.content,
        timestamp: new Date(messageData.created_at),
        agentInfo: this.agentInfo,
        id: messageData.$id || messageData.id
      });
    },

    // ✅ Process queued messages
    processQueuedMessages() {
      if (this.pendingCustomerMessages.length > 0) {
        console.log(`📤 Processing ${this.pendingCustomerMessages.length} queued messages`);
        
        this.$nextTick(() => {
          setTimeout(() => {
            this.pendingCustomerMessages.forEach(message => {
              this.sendMessageToNewChatBody(message);
            });
            this.pendingCustomerMessages = [];
          }, 500);
        });
      }
    },

    // ✅ ENHANCED: Conversation update handling with better detection
    async handleConversationUpdate(updatedConversation) {
      try {
        const newParticipantCount = updatedConversation.participants?.length || 0;
        
        console.log('📡 ChatHuman conversation update received:', {
          hasAgentName: !!updatedConversation.agentName,
          hasAgentProfile: !!updatedConversation.agentProfile,
          participantCount: newParticipantCount,
          oldParticipantCount: this.participantCount,
          hasEmittedAgentJoined: this.hasEmittedAgentJoined,
          hasProcessedInitialState: this.hasProcessedInitialState
        });

        // ✅ CRITICAL: Only process if initial state is processed AND we haven't emitted yet
        if (!this.hasProcessedInitialState) {
          console.log('📡 Conversation update during initial load - updating state silently');
          
          // Update participant count and agent info silently
          this.participantCount = newParticipantCount;
          
          if (!this.agentInfo && (updatedConversation.agentName || newParticipantCount > 1)) {
            const agentIds = updatedConversation.participants?.filter(id => id !== this.customer.anonymousId) || [];
            if (agentIds.length > 0) {
              this.agentInfo = {
                agentName: updatedConversation.agentName || 'Support Agent',
                avatar: updatedConversation.agentProfile || null,
                agentId: agentIds[0]
              };
              this.isAgentFullyJoined = true;
              this.hasEmittedAgentJoined = true; // Mark as handled
              console.log('👤 Set agent info from initial load (no join event):', this.agentInfo);
            }
          }
          return;
        }

        // ✅ Check for REAL participant increase (after initial state processed)
        const isRealIncrease = newParticipantCount > this.participantCount && 
                              this.hasProcessedInitialState && 
                              !this.hasEmittedAgentJoined;
        
        if (isRealIncrease) {
          console.log('🎉 REAL PARTICIPANT COUNT INCREASED - New agent joined!');
          
          const agentIds = updatedConversation.participants.filter(id => id !== this.customer.anonymousId);
          
          if (agentIds.length > 0) {
            const newAgentId = agentIds[agentIds.length - 1];
            console.log('👤 New agent ID detected:', newAgentId);
            
            this.agentInfo = {
              agentName: updatedConversation.agentName || 'Support Agent',
              avatar: updatedConversation.agentProfile || null,
              agentId: newAgentId
            };
            
            this.isAgentFullyJoined = true;
            this.hasEmittedAgentJoined = true;
            
            // Clear timeout
            if (this.agentDetectionTimeout) {
              clearTimeout(this.agentDetectionTimeout);
            }
            
            // ✅ EMIT AGENT JOINED EVENT
            this.$emit('agent-joined', {
              agentInfo: this.agentInfo,
              agentId: newAgentId
            });
            
            console.log('✅ Agent joined via REAL participant count increase:', this.agentInfo);
            this.processQueuedMessages();
          }
        } else {
          console.log('📡 Participant count change but not a real increase or already handled');
        }

        // ✅ Update participant count
        this.participantCount = newParticipantCount;

        // ✅ Skip further processing if already emitted
        if (this.hasEmittedAgentJoined) {
          console.log('⏭️ Skipping conversation update - agent already handled');
          return;
        }

      } catch (error) {
        console.error('❌ Error handling conversation update:', error);
      }
    },

    // ✅ Check if conversation already has agent details
    async checkConversationAgentInfo() {
      try {
        if (this.conversation.agentName || this.conversation.agentProfile) {
          console.log('👤 Found existing agent info in conversation');
          
          this.agentInfo = {
            agentName: this.conversation.agentName,
            avatar: this.conversation.agentProfile,
            agentId: 'existing-agent'
          };
          
          this.isAgentFullyJoined = true;
          this.hasEmittedAgentJoined = true; // Mark as handled - no event
          this.processQueuedMessages();
          
          console.log('✅ Using stored agent info (no join event needed):', this.agentInfo);
        }
      } catch (error) {
        console.error('❌ Error checking conversation agent info:', error);
      }
    },

    // ✅ Check existing agent
    async checkExistingAgent() {
      try {
        if (!this.agentInfo && this.conversation.participants && this.conversation.participants.length > 1) {
          console.log('👤 Agent already in conversation participants');
          this.hasEmittedAgentJoined = true; // Mark as handled for existing agents
          await this.handleAgentJoined();
        }
      } catch (error) {
        console.error('❌ Error checking existing agent:', error);
      }
    },

    // ✅ Handle agent joined (for existing agents, no event emission)
    async handleAgentJoined(updatedConversation = null) {
      try {
        const conversation = updatedConversation || this.conversation;
        
        const agentIds = conversation.participants.filter(id => id !== this.customer.anonymousId);
        
        if (agentIds.length > 0) {
          const agentId = agentIds[agentIds.length - 1];
          console.log('🔍 Found agent ID:', agentId);

          const result = await handleAgentJoinedChat(conversation, this.customer);
          
          if (result && result.agentInfo) {
            this.agentInfo = result.agentInfo;
            this.isAgentFullyJoined = true;
            this.processQueuedMessages();
            
            console.log('✅ Agent info set (existing agent, no join event):', this.agentInfo);
          }
        }

      } catch (error) {
        console.error('❌ Error handling agent joined:', error);
      }
    },

    cleanup() {
      console.log('🧹 ChatHuman cleanup');
      
      // Clear timeout
      if (this.agentDetectionTimeout) {
        clearTimeout(this.agentDetectionTimeout);
      }
      
      if (this.subscriptionUnsubscribe) {
        this.subscriptionUnsubscribe();
      }
      if (this.messageSubscription) {
        this.messageSubscription();
      }
    }
  }
}
</script>

<style scoped>
.message-item {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
